import { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MobileMapWithDrawings from '../components/map/MobileMapWithDrawings';
import GTARadar from '../components/shared/GTARadar';
import { useAppSelector } from '../store';
import type { Incident } from '../types';
import { API_BASE_URL } from '../lib/utils';
import { Target, ArrowLeft } from 'lucide-react';

// Load map drawings from localStorage for an event
function loadDrawingsForEvent(eventId: string | null): any[] {
  if (!eventId) return [];
  try {
    const storageKey = `c2_map_drawings_${eventId}`;
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export default function MobileMapPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAppSelector((state) => state.auth);
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  
  // Check if navigated from "Navigate" button OR has active assignment (radar mode)
  const hasActiveAssignment = !!activeDispatch?.incidentId;
  const startInRadarMode = searchParams.get('radar') === 'true' || hasActiveAssignment;
  
  // Debug logging
  useEffect(() => {
    console.log('MobileMapPage mounted, radar param:', searchParams.get('radar'), 'hasActiveAssignment:', hasActiveAssignment, 'startInRadarMode:', startInRadarMode);
  }, []);
  
  // User location and heading for radar
  const [userLocation, setUserLocation] = useState<{lat: number; lng: number} | null>(null);
  const [userHeading, setUserHeading] = useState<number>(0);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Get the assigned incident
  const assignedIncident = useMemo(() => {
    if (!activeDispatch?.incidentId) return null;
    return incidents.find((i) => i.id === activeDispatch.incidentId) || null;
  }, [incidents, activeDispatch]);

  // Get event ID from assignment or fallback
  const eventId = activeDispatch?.eventId || null;

  // Load drawings for this event
  const drawings = useMemo(() => loadDrawingsForEvent(eventId), [eventId]);

  // Center on assigned incident if available
  const center = useMemo((): [number, number] => {
    if (assignedIncident?.location?.latitude && assignedIncident?.location?.longitude) {
      return [assignedIncident.location.latitude, assignedIncident.location.longitude];
    }
    return [51.505, -0.09]; // Default London
  }, [assignedIncident]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/incidents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setIncidents(d.incidents || []))
      .catch(() => {});
  }, [token]);

  // Track user location and GPS heading
  const gpsHeadingRef = useRef<number | null>(null);
  const deviceHeadingRef = useRef<number>(0);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        // Use GPS course heading when moving (speed > 0.5 m/s = ~1.8 km/h)
        if (position.coords.heading !== null && position.coords.speed !== null && position.coords.speed > 0.5) {
          gpsHeadingRef.current = position.coords.heading;
          setUserHeading((position.coords.heading + 360) % 360);
        } else {
          gpsHeadingRef.current = null;
        }
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationError(null);
      },
      () => { setLocationError('Location access denied'); },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // Track device heading - raw values only, GTARadar handles smoothing via DOM
  useEffect(() => {
    let lastUpdate = 0;
    let usingAbsolute = false;
    const THROTTLE_MS = 100; // max 10 updates/sec

    const applyHeading = (raw: number) => {
      const now = Date.now();
      if (now - lastUpdate < THROTTLE_MS) return;
      lastUpdate = now;
      deviceHeadingRef.current = (raw + 360) % 360;
      setUserHeading((raw + 360) % 360);
    };

    const handleAbsolute = (event: DeviceOrientationEvent) => {
      if (gpsHeadingRef.current !== null) return;
      if (event.alpha === null) return;
      usingAbsolute = true;
      applyHeading(event.alpha);
    };

    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (gpsHeadingRef.current !== null) return;
      if (usingAbsolute) return;
      const webkitEvent = event as any;
      if (webkitEvent.webkitCompassHeading !== undefined) {
        applyHeading(webkitEvent.webkitCompassHeading);
      } else if (event.alpha !== null) {
        applyHeading(event.alpha);
      }
    };

    const setup = async () => {
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        try {
          const p = await (DeviceOrientationEvent as any).requestPermission();
          if (p !== 'granted') return;
        } catch { /* proceed anyway */ }
      }
      window.addEventListener('deviceorientationabsolute', handleAbsolute as EventListener, true);
      window.addEventListener('deviceorientation', handleOrientation, true);
    };

    setup();

    return () => {
      window.removeEventListener('deviceorientationabsolute', handleAbsolute as EventListener, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, []);

  // Build list with assigned incident first/highlighted
  const displayIncidents = useMemo(() => {
    if (!assignedIncident) return incidents;
    // Put assigned incident at the beginning for emphasis
    const others = incidents.filter((i) => i.id !== assignedIncident.id);
    return [assignedIncident, ...others];
  }, [incidents, assignedIncident]);

  const [showRadar, setShowRadar] = useState(startInRadarMode);
  const [radarFullscreen, setRadarFullscreen] = useState(startInRadarMode);
  
  // Radar-only mode (hide regular map when navigated from home)
  const [radarOnlyMode, setRadarOnlyMode] = useState(startInRadarMode);
  
  // Sync radar mode with query param changes when navigating
  useEffect(() => {
    if (startInRadarMode) {
      setShowRadar(true);
      setRadarFullscreen(true);
      setRadarOnlyMode(true);
    }
  }, [startInRadarMode]);

  return (
    <div className="h-full w-full relative">
      {/* Assigned incident banner */}
      {assignedIncident && (
        <div className="absolute top-2 left-2 right-2 z-[500] bg-card/95 border border-border rounded-lg p-2 shadow-lg">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              assignedIncident.severity === 'critical' ? 'bg-red-500' :
              assignedIncident.severity === 'high' ? 'bg-orange-500' :
              assignedIncident.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
            }`} />
            <span className="text-xs font-semibold truncate">{assignedIncident.id}</span>
            <span className="text-xs text-muted-foreground truncate flex-1">{assignedIncident.title}</span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {assignedIncident.location?.address || 'Location not set'}
          </p>
        </div>
      )}

      {/* Regular map - hidden in radar-only mode */}
      {!radarOnlyMode && (
        <MobileMapWithDrawings 
          incidents={displayIncidents} 
          center={center}
          zoom={assignedIncident ? 16 : 13}
        drawings={drawings}
        />
      )}

      {/* Drawings count indicator - only show when not in radar-only mode */}
      {!radarOnlyMode && drawings.length > 0 && (
        <div className="absolute bottom-2 left-2 z-[500] bg-card/95 border border-border rounded-lg px-2 py-1 text-[10px] text-muted-foreground">
          {drawings.length} drawing{drawings.length > 1 ? 's' : ''}
        </div>
      )}

      {/* Radar toggle button */}
      {assignedIncident?.location?.latitude && userLocation && (
        <div className="absolute bottom-4 right-2 z-[500] flex flex-col gap-2">
          <button
            onClick={() => {
              setRadarOnlyMode(true);
              setShowRadar(true);
              setRadarFullscreen(true);
            }}
            className="p-3 rounded-full shadow-lg transition-colors bg-card border border-border text-foreground"
            title="Open Radar"
          >
            <Target className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Location error message */}
      {locationError && assignedIncident?.location?.latitude && (
        <div className="absolute bottom-20 right-2 z-[500] bg-red-500/90 text-white text-xs px-3 py-2 rounded-lg max-w-[200px]">
          {locationError}
        </div>
      )}

      {/* Radar-only mode: Full screen centered radar */}
      {radarOnlyMode && assignedIncident?.location?.latitude && userLocation && (
        <div className="fixed inset-0 z-[1000] bg-black flex flex-col items-center justify-center p-4">
          {/* Back button */}
          <button
            onClick={() => {
              setRadarOnlyMode(false);
              setShowRadar(false);
              setRadarFullscreen(false);
            }}
            className="absolute top-4 left-4 z-[1100] p-3 rounded-full bg-card border border-border shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {/* Incident info at top */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1100] bg-card/95 border border-border rounded-lg px-4 py-2 shadow-lg max-w-[80%]">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                assignedIncident.severity === 'critical' ? 'bg-red-500' :
                assignedIncident.severity === 'high' ? 'bg-orange-500' :
                assignedIncident.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
              }`} />
              <span className="text-xs font-semibold">{assignedIncident.id}</span>
            </div>
            <p className="text-[10px] text-muted-foreground truncate">{assignedIncident.title}</p>
          </div>
          
          {/* Full screen radar */}
          <div className="w-full max-w-md aspect-square">
            <GTARadar
              userLat={userLocation.lat}
              userLng={userLocation.lng}
              userHeading={userHeading}
              targetLat={assignedIncident.location.latitude}
              targetLng={assignedIncident.location.longitude}
              targetName={assignedIncident.title}
              targetSeverity={assignedIncident.severity}
              onClose={() => {
                setRadarOnlyMode(false);
                setShowRadar(false);
                setRadarFullscreen(false);
              }}
              fullscreen={true}
              className="rounded-full border-4 border-border"
            />
          </div>
          
          {/* Location error */}
          {locationError && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-red-500/90 text-white text-xs px-3 py-2 rounded-lg">
              {locationError}
            </div>
          )}
        </div>
      )}

      {/* GTA Radar overlay (normal mode) */}
      {!radarOnlyMode && showRadar && assignedIncident?.location?.latitude && userLocation && (
        <div className={`${radarFullscreen ? 'fixed inset-0 z-[1000]' : 'fixed bottom-24 right-4 z-[600] w-56 h-56'}`}>
          <GTARadar
            userLat={userLocation.lat}
            userLng={userLocation.lng}
            userHeading={userHeading}
            targetLat={assignedIncident.location.latitude}
            targetLng={assignedIncident.location.longitude}
            targetName={assignedIncident.title}
            targetSeverity={assignedIncident.severity}
            onClose={() => {
              setShowRadar(false); // Hide small radar overlay to show clean full map
              setRadarFullscreen(false);
              setRadarOnlyMode(false);
            }}
            fullscreen={radarFullscreen}
            className={radarFullscreen ? '' : 'rounded-full border-4 border-border shadow-2xl'}
          />
        </div>
      )}
    </div>
  );
}
